<template>
  <div class="code-input" :class="{ 'is-disabled': disabled }" data-test="code-input">
    <input
      v-for="(digit, i) in digits"
      :key="i"
      :ref="`box${i}`"
      class="pop-auth__field code-input__box"
      :class="{ 'is-invalid': invalid }"
      :value="digit"
      inputmode="numeric"
      pattern="[0-9]*"
      :autocomplete="i === 0 ? 'one-time-code' : 'off'"
      :maxlength="length"
      :disabled="disabled"
      :aria-label="`Digit ${i + 1} of ${length}`"
      @input="onInput(i, $event)"
      @keydown="onKeydown(i, $event)"
      @paste.prevent="onPaste(i, $event)"
      @focus="$event.target.select()"
    />
  </div>
</template>

<script>
// Six single-digit boxes: typing advances, Backspace steps back, a paste fills the rest,
// and `complete` fires with the full code as soon as every box has a digit.
export default {
  name: 'CodeInput',
  props: {
    length: { type: Number, default: 6 },
    disabled: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: true },
  },
  data() {
    return { digits: Array(this.length).fill('') }
  },
  mounted() {
    if (this.autofocus) this.focus(0)
  },
  methods: {
    reset() {
      this.digits = Array(this.length).fill('')
      this.focus(0)
    },
    focus(i) {
      this.$nextTick(() => {
        const el = this.$refs[`box${Math.min(Math.max(i, 0), this.length - 1)}`]
        const input = Array.isArray(el) ? el[0] : el
        if (input) input.focus()
      })
    },
    fill(i, text) {
      const chars = String(text || '').replace(/\D/g, '').split('')
      if (!chars.length) return
      const next = [...this.digits]
      chars.slice(0, this.length - i).forEach((c, k) => { next[i + k] = c })
      this.digits = next
      const lastFilled = Math.min(i + chars.length, this.length) - 1
      this.focus(lastFilled < this.length - 1 ? lastFilled + 1 : lastFilled)
      this.$emit('change', next.join(''))
      if (next.every(Boolean)) this.$emit('complete', next.join(''))
    },
    onInput(i, event) {
      const value = event.target.value
      if (!value) {
        this.$set(this.digits, i, '')
        this.$emit('change', this.digits.join(''))
        return
      }
      this.fill(i, value)
      event.target.value = this.digits[i]
    },
    onKeydown(i, event) {
      if (event.key === 'Backspace' && !this.digits[i] && i > 0) {
        this.$set(this.digits, i - 1, '')
        this.focus(i - 1)
        event.preventDefault()
      } else if (event.key === 'ArrowLeft' && i > 0) {
        this.focus(i - 1)
      } else if (event.key === 'ArrowRight' && i < this.length - 1) {
        this.focus(i + 1)
      }
    },
    onPaste(i, event) {
      const text = (event.clipboardData || window.clipboardData).getData('text')
      this.fill(text.replace(/\D/g, '').length >= this.length ? 0 : i, text)
    },
  },
}
</script>

<style scoped>
.code-input { display: flex; gap: 8px; justify-content: center; } /* boxes cap at 54px: keep the row centred */
.code-input__box {
  flex: 1;
  min-width: 0;
  width: 0;
  max-width: 54px;
  height: 64px;
  text-align: center;
  font-size: 26px;
  font-weight: 800;
  padding: 0;
  color: #014751;
  border-radius: 14px;
}
.code-input.is-disabled .code-input__box { opacity: 0.6; }
@media (max-width: 400px) {
  .code-input { gap: 6px; }
}
</style>
